import { useAppDataStore } from '@stores/useAppDataStore';

const PARIS_NAME = 'Paris, France';

/**
 * Extract specific address component from a place object
 */
const extractComponentFromPlace = (componentName, place) => {
  let result = null;
  place.address_components.forEach((component) => {
    if (component.types.includes(componentName)) {
      result = component.long_name;
    }
  });

  return result;
};

/**
 * Enhance place object with a computed postal_code address_components entry
 * This is mainly used for cities with district and without postal code returned
 * by Google (Paris 16, Lyon, Marseille)
 */
const computePostalCode = (place) => {
  if (extractComponentFromPlace('postal_code', place) !== null) {
    return place;
  }

  const subLocality = extractComponentFromPlace('sublocality_level_1', place);
  const cityName = extractComponentFromPlace('locality', place);

  if (subLocality && cityName) {
    const digit = subLocality.match(/\d+/);

    let zipCodeStart;
    switch (cityName) {
      case 'Paris':
        zipCodeStart = '75';
        break;
      case 'Lyon':
        zipCodeStart = '69';
        break;
      case 'Marseille':
        zipCodeStart = '13';
        break;
      default:
        return place;
    }

    const zipCode = `${zipCodeStart}${digit[0].padStart(3, '0')}`;
    place.address_components.push({
      long_name: zipCode,
      short_name: zipCode,
      types: [ 'postal_code' ],
    });
    return place;
  }

  return place;
};

/**
 * Set place isLocality boolean flag according to place type & address components
 */
const computeLocalityFlag = (place) => {
  // @see https://developers.google.com/maps/documentation/geocoding/overview#Types
  // eslint-disable-next-line no-param-reassign
  place.isLocality = extractComponentFromPlace('route', place) === null
    && extractComponentFromPlace('street_number', place) === null
    && extractComponentFromPlace('neighborhood', place) === null
    && !place.types.includes('point_of_interest')
    && !place.types.includes('country') // Pays
    && !place.types.includes('administrative_area_level_1') // Régions strictes
    && !place.types.includes('administrative_area_level_2') // Départements stricts
    && !place.types.includes('colloquial_area'); // Nom alternatif
  return place;
};

const computeViewport = (place) => {
  if (place.name === PARIS_NAME) {
    // eslint-disable-next-line no-param-reassign
    place.viewport = {
      east: 2.4698,
      north: 48.9021,
      south: 48.8156,
      west: 2.2242,
    };
  }

  return place;
};

const initLib = () => {
  const { googleApiKey } = useAppDataStore();
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = `//maps.googleapis.com/maps/api/js?key=${googleApiKey}&language=${window.LOCALE}&libraries=places&v=3.31`;
  document.getElementsByTagName('body')[0].appendChild(script);
  return script;
};

export const googlePlacesHelpers = {
  extractComponentFromPlace,
  computePostalCode,
  computeLocalityFlag,
  computeViewport,
  initLib,
};
